/**
 * Client Diagnostic Context Collector
 *
 * Buffers recent network errors and console errors for the
 * "Report Problem" feature. Lightweight ring buffers with no external deps.
 */

interface NetworkErrorEntry {
  url: string;
  status: number;
  method: string;
  timestamp: number;
}

interface ConsoleErrorEntry {
  message: string;
  timestamp: number;
}

const MAX_NETWORK_ERRORS = 5;
const MAX_CONSOLE_ERRORS = 10;

const networkErrors: NetworkErrorEntry[] = [];
const consoleErrors: ConsoleErrorEntry[] = [];

export function recordNetworkError(entry: NetworkErrorEntry): void {
  networkErrors.push(entry);
  if (networkErrors.length > MAX_NETWORK_ERRORS) networkErrors.shift();
}

export function recordConsoleError(message: string): void {
  consoleErrors.push({ message: message.slice(0, 500), timestamp: Date.now() });
  if (consoleErrors.length > MAX_CONSOLE_ERRORS) consoleErrors.shift();
}

export function getRecentNetworkErrors(): NetworkErrorEntry[] {
  return [...networkErrors];
}

export function getRecentConsoleErrors(): ConsoleErrorEntry[] {
  return [...consoleErrors];
}

export function collectDiagnosticContext(): Record<string, unknown> {
  return {
    url: typeof window !== "undefined" ? window.location.href : undefined,
    route: typeof window !== "undefined" ? window.location.pathname : undefined,
    viewport:
      typeof window !== "undefined"
        ? { width: window.innerWidth, height: window.innerHeight }
        : undefined,
    userAgent:
      typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    sessionDurationMs:
      typeof performance !== "undefined"
        ? Math.round(performance.now())
        : undefined,
    timestamp: new Date().toISOString(),
    recentNetworkErrors: getRecentNetworkErrors(),
    recentConsoleErrors: getRecentConsoleErrors(),
  };
}
